import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from "../Context/auth";
import MenuLateral from '../Components/MenuPrincipal/MenuLateral';
import EtapasProducaoGrid from '../Components/etapasProducaoGrid/EtapasProducaoGrid';
import Pagination from '../Components/Pagination/Pagination';
import '../Marcas/Marcas.css'; // Reutiliza estilo das Marcas

function EtapasProducao() {
    const { currentUser, appUserData } = useAuth();
    const [etapas, setEtapas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pesquisa, setPesquisa] = useState('');
    const [textoPesquisa, setTextoPesquisa] = useState('');
    const [trigger, setTrigger] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        if (appUserData && appUserData['ler_etapas_producao'] === false) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para visualizar esta tela.',
                confirmButtonColor: '#2185c6'
            }).then(() => {
                navigate('/app/home');
            });
        }
    }, [appUserData, navigate]);

    // Paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    const apiCall = useCallback(async (url, method = 'GET', data = null) => {
        if (!currentUser) throw new Error("Usuário não autenticado.");
        const token = await currentUser.getIdToken();
        const API_URL = process.env.REACT_APP_API_URL;
        const config = {
            method,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        };
        if (data && method !== 'GET') config.body = JSON.stringify(data);
        const response = await fetch(`${API_URL}${url}`, config);
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Falha na requisição para ${url}`);
        }
        if (response.status !== 204) return response.json();
        return null;
    }, [currentUser]);

    useEffect(() => {
        const fetchEtapas = async () => {
            if (!currentUser) return;
            setLoading(true);
            try {
                const url = new URL('/api/etapas-producao', process.env.REACT_APP_API_URL);
                if (textoPesquisa) {
                    url.searchParams.append('search', textoPesquisa);
                }
                const data = await apiCall(url.pathname + url.search);
                setEtapas(data || []);
                setCurrentPage(1); // Reset page on new search
            } catch (error) {
                console.error("Erro ao buscar etapas: ", error);
                Swal.fire("Erro", "Não foi possível carregar as etapas de produção.", "error");
                setEtapas([]);
            } finally {
                setLoading(false);
            }
        };

        fetchEtapas();
    }, [currentUser, textoPesquisa, trigger, apiCall]);

    const handlePesquisar = () => setTextoPesquisa(pesquisa);
    const handleAtualizar = () => {
        setPesquisa('');
        setTextoPesquisa('');
    };

    const deleteEtapa = async (id) => {
        // Validation for Delete
        if (appUserData && appUserData['excluir_etapas_producao'] === false) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Este usuário não possui permissão de exclusão. Por favor, contate o administrador.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }

        Swal.fire({
            title: "Você tem certeza?",
            text: "Esta ação não poderá ser revertida!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#2185c6",
            cancelButtonColor: "#DC3545",
            confirmButtonText: "Sim, excluir!",
            cancelButtonText: "Cancelar"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiCall(`/api/etapas-producao/${id}`, 'DELETE');
                    setEtapas(prev => prev.filter(e => e.id !== id)); // Remove localmente para ser reativo
                    setTrigger(Date.now()); // Garante sync
                    Swal.fire("Excluído!", "A etapa de produção foi deletada.", "success");
                } catch (error) {
                    console.error("Erro ao deletar etapa:", error);
                    Swal.fire("Erro!", error.message || "Não foi possível deletar a etapa.", "error");
                }
            }
        });
    }

    // Paginação Logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentEtapas = etapas.slice(indexOfFirstItem, indexOfLastItem);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const changeItemsPerPage = (limit) => {
        setItemsPerPage(limit);
        setCurrentPage(1);
    };

    if (loading) {
        return (
            <div className="marcas-page-container" style={{ display: 'flex', paddingTop: 0 }}> {/* Usa container das Marcas */}
                <MenuLateral />
                <div className="d-flex justify-content-center align-items-center w-100" style={{ marginTop: '100px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="marcas-page-container" style={{ display: 'flex', paddingTop: 0 }}>
            <MenuLateral />

            <div className="content-with-sidebar">
                <div className="marcas-header">
                    <h1 className="marcas-title">
                        <i className="fas fa-hammer text-primary"></i>
                        Listagem de <span className="highlight">Etapas de Produção</span>
                    </h1>
                </div>

                <main className="marcas-content">
                    <div className="marcas-controls">
                        <Link
                            to="/app/nova-etapa"
                            className="btn-primary-custom me-2"
                            onClick={(e) => {
                                // Validation for Create
                                if (appUserData && appUserData['cadastrar_etapas_producao'] === false) {
                                    e.preventDefault();
                                    Swal.fire({
                                        icon: 'error',
                                        title: 'Acesso Negado',
                                        text: 'Este usuário não possui permissão de cadastro. Por favor, contate o administrador.',
                                        confirmButtonColor: '#2185c6'
                                    });
                                }
                            }}
                        >
                            <i className="fas fa-plus"></i> Nova Etapa
                        </Link>
                        <div className="search-wrapper">
                            <i className="fas fa-search search-icon"></i>
                            <input
                                type="text"
                                className="search-input-custom"
                                placeholder="Pesquisar por descrição..."
                                value={pesquisa}
                                onChange={(e) => setPesquisa(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handlePesquisar()}
                            />
                        </div>
                        <button className="btn-icon-custom" onClick={handleAtualizar} disabled={loading} title="Atualizar Lista">
                            <i className="fas fa-sync-alt"></i>
                        </button>
                    </div>

                    <div className="marcas-grid-container">
                        <EtapasProducaoGrid arrayEtapas={currentEtapas} clickDelete={deleteEtapa} userPermissions={appUserData} />
                    </div>
                </main>
            </div>

            <Pagination
                totalItems={etapas.length}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={paginate}
                onItemsPerPageChange={changeItemsPerPage}
                loading={loading}
            />
        </div>
    );
}

export default EtapasProducao;
